import React from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import { useModal } from '../../../../../store/ModalProvider';
import CustomButton from '../../CustomButton/CustomButton';
import type { AssignSelectedModalData } from '../../../../../types/modal';

/**
 * ## CustomModalAssignSelected component
 * Small confirmation modal shown when assigning selected services.
 *
 * @returns {JSX.Element | null} The rendered assign selected modal
 */
const CustomModalAssignSelected: React.FC = () => {
  const { isOpen, closeModal, modalData, modalHandlers, openModal } = useModal();
  const assignData = modalData as AssignSelectedModalData | undefined;
  const [handleConfirm, handleCancel] = modalHandlers ?? [];

  if (!assignData) {
    return null;
  }

  const handleCancelAction = () => {
    if (typeof handleCancel === 'function') {
      handleCancel(assignData);
    }
    closeModal();
  };

  const handleConfirmAction = () => {
    if (typeof handleConfirm === 'function') {
      handleConfirm(assignData);
    }
    closeModal();
    setTimeout(() => {
      openModal('Atribuicao Concluida', assignData, []);
    }, 0);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancelAction}
      sx={{ pointerEvents: 'auto', zIndex: 2000 }}
      PaperProps={{
        sx: {
          width: 'min(420px, 100%)',
          borderRadius: '2px',
          border: '1px solid rgba(134, 149, 175, 0.2)',
          boxShadow: '0 24px 60px rgba(9, 32, 86, 0.16)',
          overflow: 'hidden',
          zIndex: 2001,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px 8px',
        }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            fontFamily: 'Poppins, sans-serif',
            color: 'var(--primary-dark)',
          }}
        >
          Confirmar atribuicao
        </Typography>
      </Box>

      <Box
        sx={{
          padding: '0 20px 20px',
          display: 'grid',
          gap: '16px',
        }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            fontFamily: 'Poppins, sans-serif',
            color: 'var(--primary-dark)',
          }}
        >
          Pretende atribuir {assignData.selectedCount} servicos ao motorista{' '}
          {assignData.driverName}?
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <CustomButton
            variant="text"
            color="primary"
            onClick={handleCancelAction}
            sx={{
              height: '32px',
              minHeight: '32px',
              padding: '0 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            variant="contained"
            color="primary"
            onClick={handleConfirmAction}
            sx={{
              height: '32px',
              minHeight: '32px',
              padding: '0 16px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 6px 12px rgba(9, 32, 86, 0.18)',
            }}
          >
            Confirmar
          </CustomButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CustomModalAssignSelected;
